/**
 * How stream state lands on the rendered conversation records (spec-langchain-native-runtime 08c).
 * Pure functions; `Chat.jsx` owns the state and calls these.
 *
 * Rendered record fields:
 *  - `reply._trace`        the live trace state, only while the reply streams (dropped on `done`);
 *  - `reply.traceSteps`    the step LABELS the customer saw live, kept after `done` for the
 *                          expandable summary (`[{ id, label, status }]`);
 *  - `reply.traceSummary`  the live `summary` event, `{ steps, label: null }`;
 *  - `record.traceSummary` the persisted summary as the history read projects it (08a), which wins.
 */
import { labelSteps, normalizeTraceSummary } from "./reduceTrace";

/**
 * The next `reply` of a streaming record after one ingested message.
 * @param {object} prevReply
 * @param {object} streamState the StreamingManager entry after `ingest`
 * @param {{done?: boolean, finalText?: string, fullMessage?: string, agentIDaasId?: string}} opts
 */
export function buildStreamReply(prevReply, streamState, { done, finalText, fullMessage, agentIDaasId } = {}) {
	const reply = prevReply || {};
	const next = {
		...reply,
		type: reply.type || "msg",
		message: done ? finalText : reply.message,
		streaming: !done,
		...(done && fullMessage ? { fullMessage } : {}),
		...(agentIDaasId ? { agentIDaasId } : {}),
	};
	const trace = streamState?.trace;
	if (done) {
		delete next._streamText;
		delete next._trace;
		const steps = labelSteps(trace);
		if (steps.length > 0) next.traceSteps = steps;
		const summary = normalizeTraceSummary(trace?.summary);
		if (summary) next.traceSummary = summary;
	} else {
		next._streamText = streamState?.text ?? "";
		if (trace && !trace.off) next._trace = trace;
	}
	return next;
}

/** Whether a stream message is the `summary` trace event. */
export const isSummaryEvent = (trace) => !!trace && trace.kind === "summary" && normalizeTraceSummary(trace) !== null;

/**
 * Chat sends the `summary` event AFTER the stream's `done` (08a), when the widget no longer holds a
 * stream entry for the record: set it on the record's reply without reopening the stream. Returns
 * the same array when the conversation or record is absent, or the event is not a valid summary.
 */
export function applyTraceSummary(conversations, conversationId, recordId, trace) {
	const summary = isSummaryEvent(trace) ? normalizeTraceSummary(trace) : null;
	if (!summary || !Array.isArray(conversations)) return conversations;
	let changed = false;
	const next = conversations.map((conv) => {
		if (conv?._id !== conversationId || !Array.isArray(conv.records)) return conv;
		const records = conv.records.map((r) => {
			if (r?._id !== recordId || !r.reply) return r;
			changed = true;
			return { ...r, reply: { ...r.reply, traceSummary: summary } };
		});
		return changed ? { ...conv, records } : conv;
	});
	return changed ? next : conversations;
}

/**
 * The summary to render under a record: the persisted, history-projected `record.traceSummary`
 * first (08a), then the reply's (the live event, or an unprojected legacy shape counted by labels),
 * then — between `done` and chat's `summary` event — the number of labels this session saw live.
 */
export const resolveRecordTraceSummary = (record) =>
	normalizeTraceSummary(record?.traceSummary)
	|| normalizeTraceSummary(record?.reply?.traceSummary)
	|| normalizeTraceSummary({ steps: Array.isArray(record?.reply?.traceSteps) ? record.reply.traceSteps.length : 0 });

/**
 * The persisted record (`record_update`) is authoritative (§8): no live trace survives it. The
 * record's own `traceSummary` is what renders from then on.
 */
export function stripLiveTrace(record) {
	if (!record?.reply || !("_trace" in record.reply)) return record;
	const reply = { ...record.reply };
	delete reply._trace;
	return { ...record, reply };
}
